import type { AgentEvent } from "./agentStream";
import type { ReasoningNarrationConfig } from "./narratorConfig";
import { TRACE_MODE_OFF, TRACE_MODE_POLISHED, TRACE_MODE_RAW } from "./narratorConfig";
import { streamNarratorResponse, type NarratorStreamClient } from "./narratorClient";
import {
  buildNarratorPayload,
  getNarratorPhaseHint,
  getNarratorToolLabel,
  type NarratorToolEvent,
} from "./promptCatalog";

const NO_UPDATE_SENTINEL = "__NO_UPDATE__";
const NARRATION_MIN_REASONING_CHARS_FOR_DELTA_FLUSH = 60;
const NARRATION_MAX_DELTA_FLUSH_DELAY_MS = 180;
const NARRATION_FINALIZATION_TIMEOUT_MS = 5_000;
const SECTION_HEADER_RE = /\*\*([^*\n][\s\S]*?)\*\*/;
const SECTION_BOUNDARY_MARKER = "\n\n**";

interface ReasoningBufferSnapshot {
  rawExcerpt: string;
  hopIndex: number;
  phaseHint?: string;
  sourceEvents: NarratorToolEvent[];
}

interface ReasoningNarratorOptions {
  config: ReasoningNarrationConfig;
  userQuery: string;
  signal?: AbortSignal;
  client?: NarratorStreamClient;
  now?: () => number;
}

interface NarrationFlushResult {
  emittedText: string;
  rawExcerpt: string;
  hopIndex: number;
}

export class ReasoningNarrator {
  private readonly config: ReasoningNarrationConfig;
  private readonly userQuery: string;
  private readonly signal?: AbortSignal;
  private readonly client: NarratorStreamClient;
  private readonly now: () => number;

  private pendingReasoning = "";
  private pendingReasoningCharsSinceFlush = 0;
  private currentHopIndex = 0;
  private currentPhaseHint = "planning";
  private recentToolEvents: NarratorToolEvent[] = [];
  private lastScheduleAt: number;
  private lastEmittedText = "";
  private streamBuffer = "";
  private streamSectionOpen = false;
  private streamSectionCount = 0;
  private streamCurrentSectionTitle = "";
  private streamHeaderEmitted = false;
  private streamSentinelHoldback = "";

  constructor(options: ReasoningNarratorOptions) {
    this.config = options.config;
    this.userQuery = options.userQuery;
    this.signal = options.signal;
    this.client = options.client || streamNarratorResponse;
    this.now = options.now || (() => Date.now());
    this.lastScheduleAt = this.now();
  }

  addReasoningDelta(deltaText: string, hopIndex: number): void {
    if (!deltaText || this.signal?.aborted) return;
    this.currentHopIndex = hopIndex;
    this.pendingReasoning += deltaText;
    this.pendingReasoningCharsSinceFlush += deltaText.length;
  }

  addProgressCue(cueText: string, hopIndex: number): void {
    const text = cueText.trim();
    if (!text || this.signal?.aborted) return;
    this.currentHopIndex = hopIndex;
    const separator = this.pendingReasoning.trim().length > 0 ? "\n" : "";
    this.pendingReasoning += `${separator}${text}`;
    this.pendingReasoningCharsSinceFlush += text.length;
  }

  replacePendingReasoning(reasoningText: string, hopIndex: number): void {
    if (!reasoningText || this.signal?.aborted) return;
    this.currentHopIndex = hopIndex;
    this.pendingReasoning = reasoningText;
    this.pendingReasoningCharsSinceFlush = reasoningText.length;
  }

  recordEvent(event: AgentEvent): void {
    const phaseHint = getNarratorPhaseHint(event);
    if (phaseHint) this.currentPhaseHint = phaseHint;

    if (event.kind === "tool_call") {
      this.currentHopIndex = event.hop;
      this.recentToolEvents.push({
        source: getNarratorToolLabel(event.tool),
        status: event.status,
      });
    } else if (event.kind === "tool_result") {
      this.currentHopIndex = event.hop;
      this.recentToolEvents.push({
        source: getNarratorToolLabel(event.tool),
        status: event.ok ? "completed" : "failed",
      });
    }

    if (this.recentToolEvents.length > 16) {
      this.recentToolEvents = this.recentToolEvents.slice(-16);
    }
  }

  hasPendingReasoning(): boolean {
    return this.pendingReasoning.trim().length > 0;
  }

  shouldFlushReasoningDelta(): boolean {
    if (!this.hasPendingReasoning()) return false;
    if (this.pendingReasoningCharsSinceFlush >= NARRATION_MIN_REASONING_CHARS_FOR_DELTA_FLUSH) {
      return true;
    }
    return this.now() - this.lastScheduleAt >= NARRATION_MAX_DELTA_FLUSH_DELAY_MS;
  }

  getCurrentHopIndex(): number {
    return this.currentHopIndex;
  }

  async flushStream(
    triggerReason: string,
    onChunk: (chunk: string, hopIndex: number) => void
  ): Promise<NarrationFlushResult | null> {
    if (this.signal?.aborted || !this.hasPendingReasoning()) return null;

    const snapshot = this.snapshotAndReset();
    let emittedText = "";

    const emitReadyChunk = (chunk: string) => {
      if (!chunk) return;
      emittedText += chunk;
      onChunk(chunk, snapshot.hopIndex);
    };

    const consumeDelta = (delta: string) => {
      if (!delta || this.signal?.aborted) return;
      for (const chunk of this.extractStreamChunks(delta, false)) {
        emitReadyChunk(chunk);
      }
    };

    await this.client({
      model: this.config.model,
      payload: buildNarratorPayload({
        userQuery: this.userQuery,
        rawReasoning: snapshot.rawExcerpt,
        lastSummaryEmitted: this.lastEmittedText,
        recentToolEvents: snapshot.sourceEvents,
        phaseHint: snapshot.phaseHint,
        triggerReason,
      }),
      signal: this.signal,
      onDelta: consumeDelta,
    });

    for (const chunk of this.extractStreamChunks("", true)) {
      emitReadyChunk(chunk);
    }

    const normalized = emittedText.trim();
    if (normalized && normalized !== NO_UPDATE_SENTINEL) {
      this.lastEmittedText = normalized;
    }

    return {
      emittedText,
      rawExcerpt: snapshot.rawExcerpt,
      hopIndex: snapshot.hopIndex,
    };
  }

  private snapshotAndReset(): ReasoningBufferSnapshot {
    const snapshot = {
      rawExcerpt: this.pendingReasoning,
      hopIndex: this.currentHopIndex,
      phaseHint: this.currentPhaseHint,
      sourceEvents: this.recentToolEvents.slice(-8),
    };
    this.pendingReasoning = "";
    this.pendingReasoningCharsSinceFlush = 0;
    this.lastScheduleAt = this.now();
    return snapshot;
  }

  private extractStreamChunks(text: string, streamDone: boolean): string[] {
    this.streamBuffer += `${this.streamSentinelHoldback}${text}`;
    this.streamSentinelHoldback = "";
    this.stripNoUpdateSentinel(streamDone);

    const chunks: string[] = [];

    for (;;) {
      if (!this.streamBuffer) return chunks;

      if (!this.streamSectionOpen) {
        const headerMatch = SECTION_HEADER_RE.exec(this.streamBuffer);
        if (!headerMatch) {
          if (streamDone) {
            const fallback = this.extractFinalFreeformChunk(this.streamBuffer);
            this.streamBuffer = "";
            if (fallback) chunks.push(fallback);
          }
          return chunks;
        }

        if (headerMatch.index > 0) {
          const preamble = this.streamBuffer.slice(0, headerMatch.index);
          if (preamble) {
            chunks.push(preamble);
            this.streamSectionCount += 1;
          }
          this.streamBuffer = this.streamBuffer.slice(headerMatch.index);
        }

        const anchoredMatch = SECTION_HEADER_RE.exec(this.streamBuffer);
        if (!anchoredMatch || anchoredMatch.index !== 0) return chunks;

        const title = anchoredMatch[1].trim();
        if (!title) {
          this.streamBuffer = this.streamBuffer.slice(anchoredMatch[0].length);
          continue;
        }

        this.streamCurrentSectionTitle = title;
        this.streamHeaderEmitted = false;
        this.streamSectionOpen = true;
        this.streamBuffer = this.streamBuffer.slice(anchoredMatch[0].length);
        if (this.streamBuffer.startsWith("\n")) {
          this.streamBuffer = this.streamBuffer.slice(1);
        }
        continue;
      }

      const boundaryIndex = this.streamBuffer.indexOf(SECTION_BOUNDARY_MARKER);
      if (boundaryIndex >= 0) {
        const body = this.streamBuffer.slice(0, boundaryIndex);
        chunks.push(...this.emitSectionChunks(body));
        this.streamBuffer = this.streamBuffer.slice(boundaryIndex + 2);
        this.streamSectionOpen = false;
        this.streamCurrentSectionTitle = "";
        this.streamHeaderEmitted = false;
        continue;
      }

      const nextHeaderMatch = SECTION_HEADER_RE.exec(this.streamBuffer);
      if (nextHeaderMatch) {
        const body = this.streamBuffer.slice(0, nextHeaderMatch.index);
        chunks.push(...this.emitSectionChunks(body));
        this.streamBuffer = this.streamBuffer.slice(nextHeaderMatch.index);
        this.streamSectionOpen = false;
        this.streamCurrentSectionTitle = "";
        this.streamHeaderEmitted = false;
        continue;
      }

      if (streamDone) {
        chunks.push(...this.emitSectionChunks(this.streamBuffer));
        this.streamBuffer = "";
        this.streamSectionOpen = false;
        this.streamCurrentSectionTitle = "";
        this.streamHeaderEmitted = false;
        return chunks;
      }

      const [bodyChunk, holdback] = splitBodyWithBoundaryHoldback(this.streamBuffer);
      if (bodyChunk) {
        chunks.push(...this.emitSectionChunks(bodyChunk));
      }
      this.streamBuffer = holdback;
      return chunks;
    }
  }

  private stripNoUpdateSentinel(streamDone: boolean): void {
    this.streamBuffer = this.streamBuffer.replace(NO_UPDATE_SENTINEL, "");
    if (!this.streamBuffer) return;

    const longestPrefix = Math.min(this.streamBuffer.length, NO_UPDATE_SENTINEL.length - 1);
    for (let size = longestPrefix; size > 0; size -= 1) {
      const suffix = this.streamBuffer.slice(-size);
      if (NO_UPDATE_SENTINEL.startsWith(suffix)) {
        if (streamDone) {
          this.streamBuffer = this.streamBuffer.slice(0, -size);
          this.streamSentinelHoldback = "";
          return;
        }
        this.streamSentinelHoldback = suffix;
        this.streamBuffer = this.streamBuffer.slice(0, -size);
        return;
      }
    }
    this.streamSentinelHoldback = "";
  }

  private emitSectionChunks(body: string): string[] {
    if (!this.streamCurrentSectionTitle) return body ? [body] : [];

    const chunks: string[] = [];
    if (!this.streamHeaderEmitted) {
      const header =
        this.streamSectionCount === 0
          ? `**${this.streamCurrentSectionTitle}**\n\n`
          : `\n\n**${this.streamCurrentSectionTitle}**\n\n`;
      chunks.push(header);
      this.streamSectionCount += 1;
      this.streamHeaderEmitted = true;
    }
    if (body) chunks.push(body);
    return chunks;
  }

  private extractFinalFreeformChunk(text: string): string {
    if (!text) return "";
    const possibleHeaderStart = text.lastIndexOf("**");
    if (possibleHeaderStart >= 0) {
      const suffix = text.slice(possibleHeaderStart);
      const hasClosingMarker = suffix.length > 2 && suffix.slice(2).includes("**");
      if (!hasClosingMarker) {
        return text.slice(0, possibleHeaderStart);
      }
    }
    return text;
  }
}

function splitBodyWithBoundaryHoldback(text: string): [string, string] {
  const max = Math.min(text.length, SECTION_BOUNDARY_MARKER.length - 1);
  for (let size = max; size > 0; size -= 1) {
    const suffix = text.slice(-size);
    if (SECTION_BOUNDARY_MARKER.startsWith(suffix)) {
      return [text.slice(0, -size), suffix];
    }
  }

  const possibleHeaderStart = text.lastIndexOf("**");
  if (possibleHeaderStart >= 0) {
    const suffix = text.slice(possibleHeaderStart);
    const hasClosingMarker = suffix.length > 2 && suffix.slice(2).includes("**");
    if (!hasClosingMarker) {
      return [text.slice(0, possibleHeaderStart), suffix];
    }
  }

  return [text, ""];
}

function extractUnseenReasoningSnapshot(snapshot: string, streamedReasoning: string): string {
  if (!snapshot.trim()) return "";
  if (!streamedReasoning) return snapshot;
  if (streamedReasoning.includes(snapshot)) return "";
  if (snapshot.startsWith(streamedReasoning)) return snapshot.slice(streamedReasoning.length);
  if (snapshot.endsWith(streamedReasoning)) return snapshot.slice(0, snapshot.length - streamedReasoning.length);

  const maxOverlap = Math.min(snapshot.length, streamedReasoning.length);
  for (let size = maxOverlap; size > 0; size -= 1) {
    if (streamedReasoning.endsWith(snapshot.slice(0, size))) {
      return snapshot.slice(size);
    }
  }

  return snapshot;
}

export interface NarratingAgentEventSink {
  onAgentEvent: (event: AgentEvent) => void;
  onThinking: (thinking: string) => void;
  finalize: () => Promise<{ timedOut: boolean }>;
}

export function createNarratingAgentEventSink(options: {
  config: ReasoningNarrationConfig;
  userQuery: string;
  signal?: AbortSignal;
  onAgentEvent?: (event: AgentEvent) => void;
  onThinking: (thinking: string) => void;
  client?: NarratorStreamClient;
  log?: (message: string) => void;
  initialCue?: string;
}): NarratingAgentEventSink {
  const passthrough = (event: AgentEvent) => options.onAgentEvent?.(event);
  const mode = options.config.mode;

  if (mode === TRACE_MODE_RAW) {
    return {
      onAgentEvent: passthrough,
      onThinking: options.onThinking,
      finalize: async () => ({ timedOut: false }),
    };
  }

  if (mode === TRACE_MODE_OFF) {
    return {
      onAgentEvent: (event) => {
        if (event.kind !== "reasoning_delta") passthrough(event);
      },
      onThinking: () => {},
      finalize: async () => ({ timedOut: false }),
    };
  }

  const narrator = new ReasoningNarrator({
    config: { ...options.config, mode: TRACE_MODE_POLISHED },
    userQuery: options.userQuery,
    signal: options.signal,
    client: options.client,
  });
  let flushChain = Promise.resolve();
  let narratedThinking = "";
  let streamedReasoning = "";
  let sawReasoningDelta = false;
  let scheduledInitialFlush = false;
  let finalizationTimedOut = false;
  let flushTimer: ReturnType<typeof setTimeout> | undefined;
  const narratedToolCallIds = new Set<string>();

  const clearFlushTimer = () => {
    if (!flushTimer) return;
    clearTimeout(flushTimer);
    flushTimer = undefined;
  };

  const emitNarration = (chunk: string, hopIndex: number) => {
    if (options.signal?.aborted || finalizationTimedOut || !chunk) return;
    narratedThinking += chunk;
    options.onAgentEvent?.({ kind: "reasoning_delta", hop: hopIndex, text: chunk });
    options.onThinking(narratedThinking);
  };

  const runFlush = async (triggerReason: string) => {
    if (options.signal?.aborted || !narrator.hasPendingReasoning()) return;
    try {
      await narrator.flushStream(triggerReason, emitNarration);
    } catch (error) {
      if (options.signal?.aborted) return;
      options.log?.(
        `[NARRATOR] flush failed: ${error instanceof Error ? error.message : String(error)}`
      );
      const fallback = narratedThinking ? "" : "**Working on the slide**\nI'm reviewing the slide and preparing the next update.";
      if (fallback) emitNarration(fallback, narrator.getCurrentHopIndex());
    }
  };

  const scheduleFlush = (triggerReason: string) => {
    if (!narrator.hasPendingReasoning()) return;
    clearFlushTimer();
    flushChain = flushChain.then(() => runFlush(triggerReason));
  };

  const scheduleDelayedFlush = (triggerReason: string) => {
    if (flushTimer || !narrator.hasPendingReasoning()) return;
    flushTimer = setTimeout(() => {
      flushTimer = undefined;
      scheduleFlush(triggerReason);
    }, NARRATION_MAX_DELTA_FLUSH_DELAY_MS);
  };

  if (options.initialCue?.trim()) {
    narrator.addProgressCue(options.initialCue, 0);
    scheduleFlush("initial_cue");
  }

  return {
    onAgentEvent: (event) => {
      if (options.signal?.aborted) return;

      if (event.kind === "reasoning_delta") {
        sawReasoningDelta = true;
        streamedReasoning += event.text;
        narrator.addReasoningDelta(event.text, event.hop);
        if (!scheduledInitialFlush) {
          scheduledInitialFlush = true;
          scheduleFlush("initial_reasoning");
        } else if (narrator.shouldFlushReasoningDelta()) {
          scheduleFlush("reasoning_delta");
        } else {
          scheduleDelayedFlush("reasoning_delay");
        }
        return;
      }

      narrator.recordEvent(event);
      passthrough(event);

      if (event.kind === "tool_call") {
        if (!narratedToolCallIds.has(event.callId)) {
          narratedToolCallIds.add(event.callId);
          narrator.addProgressCue(
            `The agent is ${getNarratorToolLabel(event.tool)} now.`,
            event.hop
          );
        }
        scheduleFlush("tool_boundary");
      } else if (event.kind === "output_delta") {
        scheduleFlush("answer_boundary");
      }
    },
    onThinking: (thinking) => {
      if (options.signal?.aborted || !thinking.trim()) return;
      if (
        sawReasoningDelta &&
        narrator.hasPendingReasoning() &&
        thinking.endsWith(streamedReasoning) &&
        thinking.length > streamedReasoning.length
      ) {
        streamedReasoning = thinking;
        narrator.replacePendingReasoning(thinking, narrator.getCurrentHopIndex());
        scheduleFlush("thinking_snapshot_reconcile");
        return;
      }
      const unseenThinking = sawReasoningDelta
        ? extractUnseenReasoningSnapshot(thinking, streamedReasoning)
        : thinking;
      if (!unseenThinking.trim()) return;
      streamedReasoning =
        sawReasoningDelta && thinking.length > streamedReasoning.length
          ? thinking
          : streamedReasoning + unseenThinking;
      narrator.addReasoningDelta(unseenThinking, narrator.getCurrentHopIndex());
      scheduleFlush(sawReasoningDelta ? "thinking_snapshot_reconcile" : "thinking_summary");
    },
    finalize: async () => {
      if (options.signal?.aborted) return { timedOut: false };
      clearFlushTimer();
      scheduleFlush("stream_end");
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<"timeout">((resolve) => {
        timeoutId = setTimeout(() => resolve("timeout"), NARRATION_FINALIZATION_TIMEOUT_MS);
      });
      const completed = flushChain.then(() => "completed" as const);
      const result = await Promise.race([completed, timeout]);
      if (timeoutId) clearTimeout(timeoutId);
      if (result === "timeout") {
        finalizationTimedOut = true;
        options.log?.("[NARRATOR] finalization timed out; completing response");
        return { timedOut: true };
      }
      return { timedOut: false };
    },
  };
}
